import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Typography } from "@mui/material";
import DataTable, { DataTableProps } from "../DataTable/DataTable";
import Filters from "../../components/Filters/Filters";
import Snackbar from "../Snackbar/Snackbar";
import { downloadCSV } from "../../utils/CSVHandler";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import IWalkingSteps from "../../assets/svg/IWalkingSteps";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import TimerIcon from "@mui/icons-material/Timer";
import {
  IAutocompleteFilterProps,
  ISearchFilterConfig,
  IButtonProps,
  ICalendarProps,
  IMyLeadsCSVData,
  IClientCSVData,
} from "../../constants/interfaces";

import "./styles.scss";

interface IProps {
  tableProps: DataTableProps;
  autocompleteFilterProps?: IAutocompleteFilterProps;
  searchFilterProps?: ISearchFilterConfig;
  calendarProps?: ICalendarProps;
  buttonsProps?: IButtonProps[];
  downloadCSVButtonProps?: IButtonProps;
  idTable: string;
  idCalendar?: string;
  getCSVData?: () => Promise<IMyLeadsCSVData[] | IClientCSVData[]>;
  header?: React.ReactNode;
  isStarsReport?: React.ReactNode;
}

const Table = observer((props: IProps) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const {
    tableProps,
    autocompleteFilterProps,
    searchFilterProps,
    calendarProps,
    buttonsProps,
    downloadCSVButtonProps,
    getCSVData,
    header,
    isStarsReport,
  } = props;

  const {
    columns,
    data,
    totalRows,
    loading,
    orderBy,
    orderByDesc,
    paginationValue,
    mobileBreakpoint,
    higlightedRow,
    expandRowOnMobileBreakpoint,
    getRowCanExpand,
    renderExpandableComponent,
    handleRowClicked,
    handleTableOptionsChanged,
  } = tableProps;

  const handleDownloadCSV = async () => {
    try {
      if (getCSVData) {
        const data: IMyLeadsCSVData[] | IClientCSVData[] = await getCSVData();
        if (data?.length > 0) {
          downloadCSV(data);
        } else {
          throw new Error("No data available for download");
        }
      } else {
        throw new Error("An error occurred while downloading CSV");
      }
    } catch (error: any) {
      setErrorMessage(error?.message);
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const IconWithText = ({ icon: Icon, color, text, width, height }: any) => (
    <Box
      display="inline-block"
      alignItems="center"
      sx={{
        display: "flex",
        alignItems: "center",
        mr: 1,
      }}
    >
      <Icon
        fontSize="large"
        sx={{ color, mr: 1 }}
        style={{
          float: "left",
          marginRight: "0.3rem",
          width: width ? width : "1em",
          height: height ? height : "1em",
        }}
      />

      <Typography variant="body1" sx={{ fontSize: "1.3rem" }}>
        {text}
      </Typography>
    </Box>
  );

  const StarLegend = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        px: 2,
        pt: 1,
        pb: 2,
      }}
    >
      <Box sx={{ fontSize: "1.4rem", mb: 1 }}>Stars over the timeframe earned from:</Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <IconWithText icon={FitnessCenterIcon} color="#651FFF" text="Workout" />

        <IconWithText icon={RestaurantIcon} color="#D50000" text="Food" />

        <IconWithText icon={IWalkingSteps} width="1.3em" height="1.3em" color="#651FFF" text="Walking" />

        <IconWithText icon={TimerIcon} color="#0000006B" text="Fasting" />

        <IconWithText icon={WaterDropIcon} color="#1E88E5" text="Water" />
      </Box>
    </Box>
  );
  return (
    <Box sx={{ width: "100%", height: "100%", p: 1 }} id={props.idTable}>
      <Filters
        autocompleteFilterProps={autocompleteFilterProps}
        searchFilterProps={searchFilterProps}
        calendarProps={calendarProps}
        buttonsProps={buttonsProps}
        downloadCSVButtonProps={downloadCSVButtonProps}
        onDownloadCSV={handleDownloadCSV}
        idCalendar={props.idCalendar}
      />

      {header && <Box>{header}</Box>}

      <Box>
        <DataTable
          columns={columns}
          data={data}
          totalRows={totalRows ?? 0}
          loading={loading}
          paginationValue={paginationValue}
          orderBy={orderBy}
          orderByDesc={orderByDesc}
          mobileBreakpoint={mobileBreakpoint}
          higlightedRow={higlightedRow}
          expandRowOnMobileBreakpoint={expandRowOnMobileBreakpoint}
          getRowCanExpand={getRowCanExpand}
          renderExpandableComponent={renderExpandableComponent}
          handleTableOptionsChanged={handleTableOptionsChanged}
          handleRowClicked={handleRowClicked}
        ></DataTable>
      </Box>
      {!tableProps.loading && isStarsReport && <StarLegend />}
      <Snackbar open={openSnackbar} onClose={handleCloseSnackbar} severity="error" message={errorMessage} />
    </Box>
  );
});

export default Table;
